import type {
  AgentMode,
  ConversationSummary,
  MessageSummary,
  PreferenceGateResult,
} from '@tabbio/types';
import { api } from './api';

interface WorkspaceScope {
  mode?: AgentMode;
  companyId?: string;
}

export type GateDeniedPayload = Extract<PreferenceGateResult, { allowed: false }>;

// Errors from gated endpoints carry the server's error code plus the gate
// payload (from res.meta) so callers can route to PreferenceGatedScreen
// instead of showing a generic failure.
export interface GateAwareError extends Error {
  code?: string;
  gate?: GateDeniedPayload;
}

function gateAwareError(
  fallback: string,
  error: { code: string; message: string } | null,
  meta: unknown,
): GateAwareError {
  const err: GateAwareError = new Error(error?.message ?? fallback);
  if (error?.code) err.code = error.code;
  if (
    meta &&
    (error?.code === 'RESTRICTED_BY_PREFS' || error?.code === 'BLOCKED')
  ) {
    err.gate = meta as GateDeniedPayload;
  }
  return err;
}

function workspaceHeaders(scope?: WorkspaceScope) {
  if (!scope) return undefined;
  const headers: Record<string, string> = {};
  if (scope.mode) headers['x-agent-mode'] = scope.mode;
  if (scope.companyId) headers['x-company-id'] = scope.companyId;
  return Object.keys(headers).length ? { headers } : undefined;
}

export async function fetchConversations(opts?: {
  cursor?: string;
  limit?: number;
  mode?: AgentMode;
  companyId?: string;
}): Promise<{ conversations: ConversationSummary[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  if (opts?.cursor) params.set('cursor', opts.cursor);
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.mode) params.set('mode', opts.mode);
  if (opts?.companyId) params.set('companyId', opts.companyId);
  const qs = params.toString();
  const res = await api.get<ConversationSummary[]>(
    `/api/messaging/conversations${qs ? `?${qs}` : ''}`,
    workspaceHeaders({ mode: opts?.mode, companyId: opts?.companyId }),
  );
  if (res.error) throw new Error(res.error.message);
  return {
    conversations: res.data ?? [],
    nextCursor: (res as any).meta?.nextCursor ?? null,
  };
}

export async function fetchConversation(
  conversationId: string,
): Promise<ConversationSummary> {
  const res = await api.get<ConversationSummary>(
    `/api/messaging/conversations/${conversationId}`,
  );
  if (res.error || !res.data) throw new Error(res.error?.message ?? 'Not found');
  return res.data;
}

export async function fetchMessages(
  conversationId: string,
  opts?: { before?: string; limit?: number },
): Promise<{ messages: MessageSummary[]; hasMore: boolean }> {
  const params = new URLSearchParams();
  if (opts?.before) params.set('before', opts.before);
  if (opts?.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const res = await api.get<MessageSummary[]>(
    `/api/messaging/conversations/${conversationId}/messages${qs ? `?${qs}` : ''}`,
  );
  if (res.error) throw new Error(res.error.message);
  return {
    messages: res.data ?? [],
    hasMore: (res as any).meta?.hasMore ?? false,
  };
}

export async function createConversation(
  targetUserId: string,
  opts?: {
    mode?: AgentMode;
    companyId?: string;
    jobId?: string;
  },
): Promise<ConversationSummary> {
  const res = await api.post<ConversationSummary>(
    '/api/messaging/conversations',
    {
      targetUserId,
      companyId: opts?.companyId,
      jobId: opts?.jobId,
    },
    workspaceHeaders({ mode: opts?.mode, companyId: opts?.companyId }),
  );
  if (res.error || !res.data) {
    throw gateAwareError('Failed to create', res.error, (res as any).meta);
  }
  return res.data;
}

export async function sendMessageREST(
  conversationId: string,
  content: string,
  type?: string,
): Promise<MessageSummary> {
  const res = await api.post<MessageSummary>(
    `/api/messaging/conversations/${conversationId}/messages`,
    { content, type },
  );
  if (res.error || !res.data) {
    throw gateAwareError('Failed to send', res.error, (res as any).meta);
  }
  return res.data;
}

export async function markMessagesRead(
  conversationId: string,
  messageIds: string[],
): Promise<void> {
  const res = await api.post(`/api/messaging/conversations/${conversationId}/read`, {
    messageIds,
  });
  if (res.error) throw new Error(res.error.message);
}

export async function deleteConversation(conversationId: string): Promise<void> {
  const res = await api.delete(`/api/messaging/conversations/${conversationId}`);
  if (res.error) throw new Error(res.error.message);
}

// Backstop dedupe so a buggy caller cannot flood the backend even if it calls
// fetchUnreadCount in a tight loop. Returns the most-recent value seen for the
// same scope within MIN_INTERVAL_MS, and coalesces concurrent calls.
const UNREAD_MIN_INTERVAL_MS = 1500;
const lastUnreadFetch = new Map<
  string,
  { at: number; value: number; pending?: Promise<number> }
>();

export async function fetchUnreadCount(opts?: {
  mode?: AgentMode;
  companyId?: string;
}): Promise<number> {
  const cacheKey = `${opts?.mode ?? ''}|${opts?.companyId ?? ''}`;
  const cached = lastUnreadFetch.get(cacheKey);
  const now = Date.now();
  if (cached?.pending) return cached.pending;
  if (cached && now - cached.at < UNREAD_MIN_INTERVAL_MS) return cached.value;

  const params = new URLSearchParams();
  if (opts?.mode) params.set('mode', opts.mode);
  if (opts?.companyId) params.set('companyId', opts.companyId);
  const qs = params.toString();
  const pending = (async () => {
    const res = await api.get<{ count: number }>(
      `/api/messaging/unread-count${qs ? `?${qs}` : ''}`,
      workspaceHeaders({ mode: opts?.mode, companyId: opts?.companyId }),
    );
    if (res.error) throw new Error(res.error.message);
    const value = res.data?.count ?? 0;
    lastUnreadFetch.set(cacheKey, { at: Date.now(), value });
    return value;
  })();
  lastUnreadFetch.set(cacheKey, {
    at: cached?.at ?? 0,
    value: cached?.value ?? 0,
    pending,
  });
  try {
    return await pending;
  } finally {
    const entry = lastUnreadFetch.get(cacheKey);
    if (entry?.pending === pending) {
      lastUnreadFetch.set(cacheKey, { at: entry.at, value: entry.value });
    }
  }
}

// ─── User Search ────────────────────────────────────────────────

export interface UserSearchResult {
  id: string;
  name: string;
  username: string;
  image: string | null;
}

export async function searchUsers(query: string): Promise<UserSearchResult[]> {
  if (!query.trim()) return [];
  const res = await api.get<UserSearchResult[]>(
    `/api/users/search?query=${encodeURIComponent(query)}`,
  );
  return res.data ?? [];
}
